/**
 * Time Service for Server Time Synchronization
 * 
 * Provides Romanian server time instead of relying on client device time
 * Ensures consistent time across all devices and screens
 */

import React from 'react';
import { apiService } from './api';

// Use the same API base URL as the main API service
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://munca-la-schimb-production.up.railway.app/api';

export interface ServerTimeResponse {
  utcTime: string;
  romanianTime: string;
  timeZone: string;
  timeZoneDisplayName: string;
  isDaylightSavingTime: boolean;
  offset: string;
  timestamp: number;
  formattedTime: string;
  formattedDate: string;
  formattedDateTime: string;
  isoDateTime: string;
  isInAutomaticBreakTime: boolean;
  automaticBreakMessage: string;
  dayOfWeek: string;
  dayOfYear: number;
  weekOfYear: number;
}

export interface TimeSyncResponse {
  serverTimestamp: number;
  romanianTimestamp: number;
  utcTime: string;
  romanianTime: string;
  timeZoneOffset: string;
  syncId: string;
  requestReceived: string;
}

export interface AutomaticBreakStatus {
  romanianTime: string;
  formattedTime: string;
  isInAutomaticBreakTime: boolean;
  message: string;
  breakStartTime: string;
  breakEndTime: string;
  nextBreakStartTime: string;
  minutesUntilChange: number;
}

class TimeService {
  private serverTimeOffset: number = 0;
  private lastSyncTime: number = 0;
  private syncIntervalId: NodeJS.Timeout | null = null;
  private isInitialized = false;

  /**
   * Initialize the time service with server synchronization
   */
  async initialize(): Promise<void> {
    try {
      console.log('TimeService: Initializing server time synchronization');
      await this.syncWithServer();
      
      // Sync with server every 5 minutes to maintain accuracy
      this.syncIntervalId = setInterval(() => {
        this.syncWithServer().catch(console.error);
      }, 5 * 60 * 1000);
      
      this.isInitialized = true;
      console.log('TimeService: Initialization complete');
    } catch (error) {
      console.error('TimeService: Failed to initialize:', error);
      // Continue with client time if server sync fails
      this.isInitialized = true;
    }
  }

  /**
   * Synchronize local time with server time
   */
  private async syncWithServer(): Promise<void> {
    try {
      const requestTime = Date.now();
      const response = await fetch(`${API_BASE_URL}/time/sync`);
      const responseTime = Date.now();
      const roundTripTime = responseTime - requestTime;
      
      if (!response.ok) {
        throw new Error(`Server time sync failed: ${response.status}`);
      }
      
      const syncData: TimeSyncResponse = await response.json();
      const serverTime = new Date(syncData.romanianTime).getTime();
      const estimatedServerTime = serverTime + (roundTripTime / 2);
      
      this.serverTimeOffset = estimatedServerTime - Date.now();
      this.lastSyncTime = Date.now();
      
      console.log(`TimeService: Synced with server. Offset: ${this.serverTimeOffset}ms, RTT: ${roundTripTime}ms`);
    } catch (error) {
      console.error('TimeService: Server sync failed:', error);
      // Keep previous offset if sync fails
    }
  }

  /**
   * Get current Romanian time from server (with local offset adjustment)
   */
  getCurrentRomanianTime(): Date {
    if (!this.isInitialized) {
      console.warn('TimeService: Not initialized, using client time');
      return new Date();
    }
    
    // Apply server offset to current time
    const adjustedTime = new Date(Date.now() + this.serverTimeOffset);
    return adjustedTime;
  }

  /**
   * Get current server time information
   */
  async getCurrentServerTime(): Promise<ServerTimeResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/time/current`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch server time: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('TimeService: Failed to get server time:', error);
      
      // Fallback to local time with estimated offset
      const romanianTime = this.getCurrentRomanianTime();
      return {
        utcTime: new Date().toISOString(),
        romanianTime: romanianTime.toISOString(),
        timeZone: 'Europe/Bucharest',
        timeZoneDisplayName: 'Romanian Time',
        isDaylightSavingTime: false,
        offset: '+02:00',
        timestamp: Date.now(),
        formattedTime: romanianTime.toTimeString().substring(0, 8),
        formattedDate: romanianTime.toISOString().split('T')[0],
        formattedDateTime: romanianTime.toISOString().replace('T', ' ').substring(0, 19),
        isoDateTime: romanianTime.toISOString(),
        isInAutomaticBreakTime: this.isInAutomaticBreakTime(romanianTime),
        automaticBreakMessage: 'Server not available',
        dayOfWeek: romanianTime.toLocaleDateString('en-US', { weekday: 'long' }),
        dayOfYear: Math.floor((romanianTime.getTime() - new Date(romanianTime.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)),
        weekOfYear: Math.ceil(((romanianTime.getTime() - new Date(romanianTime.getFullYear(), 0, 1).getTime()) / 86400000 + new Date(romanianTime.getFullYear(), 0, 1).getDay() + 1) / 7)
      };
    }
  }

  /**
   * Get automatic break status from server
   */
  async getAutomaticBreakStatus(): Promise<AutomaticBreakStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/time/automatic-break-status`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch break status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('TimeService: Failed to get break status:', error);
      
      // Fallback to local calculation
      const romanianTime = this.getCurrentRomanianTime();
      return {
        romanianTime: romanianTime.toISOString(),
        formattedTime: romanianTime.toTimeString().substring(0, 8),
        isInAutomaticBreakTime: this.isInAutomaticBreakTime(romanianTime),
        message: 'Server not available',
        breakStartTime: new Date(romanianTime.getFullYear(), romanianTime.getMonth(), romanianTime.getDate(), 12, 0, 0).toISOString(),
        breakEndTime: new Date(romanianTime.getFullYear(), romanianTime.getMonth(), romanianTime.getDate(), 13, 0, 0).toISOString(),
        nextBreakStartTime: new Date(romanianTime.getFullYear(), romanianTime.getMonth(), romanianTime.getDate() + 1, 12, 0, 0).toISOString(),
        minutesUntilChange: 0
      };
    }
  }

  /**
   * Format Romanian time for display
   */
  formatRomanianTime(format: 'time' | 'date' | 'datetime' | 'iso' = 'time'): string {
    const romanianTime = this.getCurrentRomanianTime();
    
    switch (format) {
      case 'time':
        return romanianTime.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      case 'date':
        return romanianTime.toLocaleDateString('ro-RO');
      case 'datetime':
        return romanianTime.toLocaleString('ro-RO');
      case 'iso':
        return romanianTime.toISOString();
      default:
        return romanianTime.toLocaleTimeString('ro-RO');
    }
  }

  /**
   * Get current timestamp for API calls (should be used instead of Date.now())
   */
  getCurrentTimestamp(): string {
    return this.getCurrentRomanianTime().toISOString();
  }

  /**
   * Get current date string for API calls
   */
  getCurrentDateString(): string {
    const romanianTime = this.getCurrentRomanianTime();
    return romanianTime.toISOString().split('T')[0];
  }

  /**
   * Check if current Romanian time is in automatic break period (local fallback)
   */
  private isInAutomaticBreakTime(romanianTime: Date): boolean {
    const hour = romanianTime.getHours();
    return hour >= 12 && hour < 13;
  }

  /**
   * Convert UTC time string to Romanian time
   */
  async convertUtcToRomanian(utcTimeString: string): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/time/convert-utc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ utcTime: utcTimeString }),
      });
      
      if (!response.ok) {
        throw new Error(`Time conversion failed: ${response.status}`);
      }
      
      const result = await response.json();
      return result.romanianTime;
    } catch (error) {
      console.error('TimeService: UTC conversion failed:', error);
      // Fallback to local conversion
      return new Date(utcTimeString).toISOString();
    }
  }

  /**
   * Get sync status information
   */
  getSyncStatus(): { isInitialized: boolean; lastSyncTime: number; serverOffset: number } {
    return {
      isInitialized: this.isInitialized,
      lastSyncTime: this.lastSyncTime,
      serverOffset: this.serverTimeOffset
    };
  }

  /**
   * Force a manual sync with server
   */
  async forceSyncWithServer(): Promise<void> {
    await this.syncWithServer();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }
    this.isInitialized = false;
    console.log('TimeService: Service destroyed');
  }
}

// Create and export singleton instance
export const timeService = new TimeService();

// React hook for using time service
export const useServerTime = () => {
  const [currentTime, setCurrentTime] = React.useState<Date>(new Date());
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    // Initialize time service
    const initTimeService = async () => {
      if (!timeService.getSyncStatus().isInitialized) {
        await timeService.initialize();
      }
      setIsInitialized(true);
    };

    initTimeService();

    // Update current time every second
    const intervalId = setInterval(() => {
      setCurrentTime(timeService.getCurrentRomanianTime());
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return {
    currentTime,
    isInitialized,
    formatTime: (format?: 'time' | 'date' | 'datetime' | 'iso') => timeService.formatRomanianTime(format),
    getCurrentTimestamp: () => timeService.getCurrentTimestamp(),
    getCurrentDateString: () => timeService.getCurrentDateString(),
    syncStatus: timeService.getSyncStatus(),
    forceSync: () => timeService.forceSyncWithServer()
  };
};

export default timeService;